import React, { useEffect } from 'react';
import Table from '@mui/material/Table';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import { MAIN_THEME } from '@styles';
import { IAbstractRecord } from '@models';
import { IListingData } from '@models/listing';
import {
  TableSortOrder,
  convertRecordsIntoEnhancedRows,
} from '@utils/table-utils';
import { EnhancedTableHeader } from '../widgets/shared-with-old';
import EnhancedTableBody from '../widgets/shared-with-old/EnhancedTableBody';
import EnhancedTableHead from '../widgets/shared-with-old/EnhancedTableHead';
import { makeStyles } from 'tss-react/mui';
import {
  EnhancedTableFilterType,
  IEnhancedMenuItem,
  IFilterModel,
  ITableRowSettings,
} from '../types';
import { CDN_URL } from '@utils';
import ListingTableCollapsableToolbar from '@components/table/with-collapsable-filters/ListingTableCollapsableToolbar';

export interface IListingTableWithCollapsibleFiltersProps {
  name: string;
  entityName?: string;
  headers: Record<string, EnhancedTableHeader>;
  rowSettings?: ITableRowSettings;
  actions: IEnhancedMenuItem[];
  data: IListingData;
  inline?: boolean;
  inlineTitle?: string;
  dropdownActionsNames?: string[];
  defaultOrderByColumn?: string;
  orderByAscendingByDefault?: boolean;
  secondOrderByColumn?: string;
  secondOrderByAscendingByDefault?: boolean;
  disableSelection?: boolean;
  context?: IAbstractRecord;
  loader?: boolean;
  onSelectionChange?: (selectedRows: string[]) => void;
  pageSizeOptions?: number[];
  tableFilters?: Record<string, EnhancedTableFilterType>;
  handleFilterChange?: (
    name: string,
    value: string[] | Date[],
    type: string
  ) => void;
  handleFilterAction?: () => void;
  handleFilterClear?: () => void;
  onPageChange?: (filterModel: IFilterModel) => void;
}

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    boxShadow: `0px 0px 6px ${MAIN_THEME.color.white86}`,
    margin: '0 auto',
  },
  toolbarPaper: {
    margin: '0 0 1em',
  },
  separator: {
    height: 2,
    margin: 0,
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  header: {
    margin: '0 0 1em',
    display: 'grid',
    gridTemplateAreas: `'title actions'`,
    gridGap: '1em',
  },
  title: {
    display: 'block',
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.lg,
    fontFamily: MAIN_THEME.font.medium,
    letterSpacing: 0,
    color: MAIN_THEME.color.black,
    fontWeight: 'normal',
    margin: '0 0 17.5px 0',
    gridArea: 'title',
    alignSelf: 'center',
    // textTransform: 'uppercase',
    width: '450px',
  },
  actionsContainer: {
    margin: '0 3px 0 auto',
  },
  action: {
    '& span': {
      margin: '0.5em auto 0',
    },
  },
  button: {
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
  },
  heightAndCenter: {
    height: '500px',
    justifyContent: 'center',
  },
}));

const defaultpageSizeOptions = [5, 10, 25];

const ListingTableWithCollapsibleFilters: React.FC<
  IListingTableWithCollapsibleFiltersProps
> = ({
  name,
  headers,
  rowSettings,
  data,
  actions,
  inlineTitle,
  dropdownActionsNames = [],
  defaultOrderByColumn,
  orderByAscendingByDefault,
  secondOrderByColumn,
  secondOrderByAscendingByDefault,
  tableFilters,
  handleFilterClear,
  handleFilterAction,
  handleFilterChange,
  entityName = '',
  disableSelection,
  context,
  loader,
  onSelectionChange = undefined,
  onPageChange,
  pageSizeOptions = defaultpageSizeOptions,
}) => {
  const { classes } = useStyles();
  const dataKeys = Object.keys(data.pagedItems);

  const [order, setOrder] = React.useState<TableSortOrder>();
  const [orderBy, setOrderBy] = React.useState<string>();
  const [secondOrder, setSecondOrder] = React.useState<TableSortOrder>();
  const [selected, setSelected] = React.useState<string[]>([]);
  const currentName = React.useRef<string>();

  const pageNumber = data.pageNumber - 1;
  const rowCount = data.totalCount || 0;

  useEffect(() => {
    if (currentName.current !== name && defaultOrderByColumn) {
      currentName.current = name;
      setOrder(orderByAscendingByDefault ? 'asc' : 'desc');
      setOrderBy(defaultOrderByColumn);
      if (secondOrderByColumn) {
        setSecondOrder(secondOrderByAscendingByDefault ? 'asc' : 'desc');
      }
    }
  }, [
    defaultOrderByColumn,
    name,
    orderByAscendingByDefault,
    secondOrderByAscendingByDefault,
    secondOrderByColumn,
  ]);

  function handleRequestSort(
    event: React.MouseEvent<unknown>,
    property: string
  ) {
    const isDesc = orderBy === property && order === 'desc';
    const newOrder = isDesc ? 'asc' : 'desc';

    setOrder(newOrder);
    setOrderBy(property);

    onPageChange?.({
      pagination: {
        pageNumber: pageNumber + 1,
        pageSize: data.pageSize,
      },
      orderBy: property,
      descending: newOrder === 'desc',
    });
  }

  function handleSelectAllClick(rowName: string, checked: boolean) {
    if (checked) {
      setSelected(dataKeys);
      if (onSelectionChange != undefined) {
        onSelectionChange(selected);
      }

      return;
    }
    setSelected([]);
    if (onSelectionChange != undefined) {
      onSelectionChange([]);
    }
  }

  function handleClick(rowName: string) {
    const selectedIndex = selected.indexOf(rowName);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, rowName);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
    if (onSelectionChange != undefined) {
      onSelectionChange(newSelected);
    }
  }

  function handleChangePage(event: unknown, newPage: number) {
    onPageChange?.({
      pagination: {
        pageNumber: newPage + 1,
        pageSize: data.pageSize,
      },
      orderBy,
      descending: order === 'desc',
    });
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    const newRowsPerPage = Number(event.target.value);
    onPageChange?.({
      pagination: {
        pageNumber: 1,
        pageSize: newRowsPerPage,
      },
      orderBy,
      descending: order === 'desc',
    });

    onPageChange?.({
      pagination: {
        pageNumber: 1,
        pageSize: newRowsPerPage,
      },
      orderBy,
      descending: order === 'desc',
    });
  }

  const getRows = () => {
    return convertRecordsIntoEnhancedRows(data.pagedItems);
  };

  const isSelected = (key: string) => selected.indexOf(key) !== -1;

  const emptyRows = data.pageSize - dataKeys.length;

  const renderToolbar = () => (
    <ListingTableCollapsableToolbar
      dropdownActionsNames={dropdownActionsNames}
      selected={selected}
      actions={actions}
      data={data.pagedItems}
      cdnUrl={CDN_URL}
      title={inlineTitle}
      handleFilterChange={(name, value, type) =>
        handleFilterChange(name, value, type)
      }
      filters={tableFilters}
      context={context}
      onFilterClear={handleFilterClear}
      onFilterAction={handleFilterAction}
    />
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        {renderToolbar()}
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={dataKeys.length}
              headRows={headers}
              disableSelection={disableSelection}
            />
            <EnhancedTableBody
              entityName={entityName}
              emptyRows={emptyRows}
              pageSize={data.pageSize}
              handleClick={handleClick}
              headers={headers}
              rowSettings={rowSettings}
              isSelected={isSelected}
              rows={getRows()}
              disableSelection={disableSelection}
              loader={loader}
            />
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={pageSizeOptions || defaultpageSizeOptions}
          component="div"
          count={rowCount}
          rowsPerPage={data.pageSize}
          page={pageNumber}
          backIconButtonProps={{
            'aria-label': 'Previous',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next',
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage=""
        />
      </Paper>
    </div>
  );
};
export default ListingTableWithCollapsibleFilters;
